import { useEffect, useState, useMemo, useCallback } from 'react';
import { FiUser, FiSearch, FiRefreshCw, FiFilter, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import Pagination from '../components/Pagination';
import { CRUD_UI } from '../components/crudUi';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const Patients = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterCccd, setFilterCccd] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    q: '',
    patient_name: '',
    cccd: '',
  });

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (appliedFilters.q) params.q = appliedFilters.q;
      if (appliedFilters.patient_name) params.patient_name = appliedFilters.patient_name;
      if (appliedFilters.cccd) params.cccd = appliedFilters.cccd;

      const response = await api.get('/site/patients', { params });
      if (!response.data?.success) {
        throw new Error(response.data?.error?.message || 'Không thể tải danh sách bệnh nhân');
      }

      setPatients(response.data.data || []);
      setTotalItems(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Fetch patients error:', error);
      toast.error('Không thể tải danh sách bệnh nhân');
      setPatients([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, [currentPage, itemsPerPage, appliedFilters]);

  const applyFilter = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setAppliedFilters({
      q: searchText.trim(),
      patient_name: filterName.trim(),
      cccd: filterCccd.trim(),
    });
  };

  const clearFilter = () => {
    setSearchText('');
    setFilterName('');
    setFilterCccd('');
    setCurrentPage(1);
    setAppliedFilters({ q: '', patient_name: '', cccd: '' });
  };

  const hasActiveFilter = appliedFilters.q || appliedFilters.patient_name || appliedFilters.cccd;

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  }, []);

  if (loading && patients.length === 0) {
    return (
      <div className={CRUD_UI.loadingWrap}>
        <div className={CRUD_UI.loadingSpinner}></div>
        <p className="text-slate-500 text-sm">Đang tải danh sách bệnh nhân...</p>
      </div>
    );
  }

  return (
    <div className={CRUD_UI.page}>
      {/* Filter Card */}
      <div className={CRUD_UI.filterCard}>
        <div className={`${CRUD_UI.filterHeader} justify-between flex-wrap gap-2`}>
          <div className="flex items-center gap-2">
            <FiFilter className="text-slate-500 text-xl" />
            <span className="text-sm font-semibold text-slate-900">Tìm kiếm và bộ lọc</span>
          </div>
        </div>
        <form onSubmit={applyFilter} className="flex gap-4 flex-wrap">
          <div className={CRUD_UI.filterFieldWide}>
            <label className={CRUD_UI.filterLabel}>Tìm nhanh</label>
            <div className={CRUD_UI.searchWrap}>
              <FiSearch className={CRUD_UI.searchIcon} />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className={CRUD_UI.searchInput}
                placeholder="Mã BN, tên, CCCD, SĐT..."
              />
              {searchText && (
                <button
                  type="button"
                  className={CRUD_UI.searchClearButton}
                  onClick={() => setSearchText('')}
                  title="Xóa tìm kiếm"
                >
                  <FiX className="text-base" />
                </button>
              )}
            </div>
          </div>

          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Họ tên</label>
            <input
              type="text"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className={CRUD_UI.filterInput}
              placeholder="Nhập họ tên bệnh nhân"
            />
          </div>

          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Số CCCD</label>
            <input
              type="text"
              value={filterCccd}
              onChange={(e) => setFilterCccd(e.target.value)}
              className={CRUD_UI.filterInput}
              placeholder="Nhập CCCD"
            />
          </div>

          <div className="flex items-end gap-2">
            <button type="submit" className={CRUD_UI.primaryButton}>
              <FiSearch className="text-sm" />
              <span>Tìm</span>
            </button>
            <button
              type="button"
              className={CRUD_UI.secondaryButton}
              onClick={clearFilter}
              disabled={!hasActiveFilter && !searchText && !filterName && !filterCccd}
            >
              <FiRefreshCw className="text-sm" />
              <span>Xóa lọc</span>
            </button>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className={CRUD_UI.tableViewportShell}>
        <div className={CRUD_UI.tableViewportScroll}>
          <table className={CRUD_UI.table}>
            <thead className={CRUD_UI.tableHead}>
              <tr>
                <th className={CRUD_UI.tableHeaderCell}>Bệnh nhân</th>
                <th className={CRUD_UI.tableHeaderCell}>CCCD</th>
                <th className={CRUD_UI.tableHeaderCell}>Giới tính / Ngày sinh</th>
                <th className={CRUD_UI.tableHeaderCell}>Liên hệ</th>
                <th className={CRUD_UI.tableHeaderCell}>Địa chỉ</th>
                <th className={CRUD_UI.tableHeaderCell}>Số ca chụp</th>
                <th className={CRUD_UI.tableHeaderCell}>Ca gần nhất</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className={CRUD_UI.emptyCell}>
                    <div className="flex flex-col items-center gap-2">
                      <div className={CRUD_UI.loadingSpinner}></div>
                      <p className="text-slate-500 text-sm">Đang tải...</p>
                    </div>
                  </td>
                </tr>
              ) : patients.length === 0 ? (
                <tr>
                  <td colSpan={7} className={CRUD_UI.emptyCell}>
                    <div className="flex flex-col items-center gap-2">
                      <FiUser className="text-5xl text-slate-300" />
                      <p className="text-slate-500 text-sm">Không có dữ liệu bệnh nhân</p>
                    </div>
                  </td>
                </tr>
              ) : (
                patients.map((patient) => (
                  <tr key={patient.id} className={CRUD_UI.tableRow}>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-start gap-2">
                        <FiUser className="text-slate-400 mt-0.5 shrink-0" />
                        <div>
                          <div className="font-semibold text-slate-900">{patient.patient_name || '-'}</div>
                          <div className="text-xs text-slate-500">Mã BN: {patient.patient_id || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">
                      {patient.cccd ? (
                        <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">
                          {patient.cccd}
                        </code>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="text-slate-700">
                        {patient.patient_sex === 'M' ? 'Nam' : patient.patient_sex === 'F' ? 'Nữ' : patient.patient_sex || '-'}
                      </div>
                      <div className="text-xs text-slate-500">{formatDate(patient.patient_birth_date) || '-'}</div>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">
                      {patient.patient_phone || <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700 max-w-[200px] truncate" title={patient.patient_address}>
                      {patient.patient_address || <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700">
                        {patient.exam_count || 0}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">
                      {formatDateTime(patient.latest_exam_time) || <span className="text-slate-400">-</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className={CRUD_UI.paginationDock}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        </div>
      )}
    </div>
  );
};

export default Patients;
